import React, {useEffect, useState, useRef} from 'react'
import {Box, Stack, Text, useMultiStyleConfig, Button, Flex, Select} from '@chakra-ui/react'
import {useParams, Link} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {remCalc} from '../../theme/_utils'
import SubscriptionCancelItemCard from '../../components/subscriptions-box/subscription-cancel-item-card'
import {ChevronDown} from '../../components/icons'
import SubscriptionCancellationModal from '../../components/subscriptions-box/subscription-cancellation-modal'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '../../components/loading-spinner'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

const SubscriptionCancel = () => {
    const {subscriptionId} = useParams()
    const [subscriptionDetails, setSubscriptionDetails] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedReason, setSelectedReason] = useState('')
    const appOrigin = useOrigin(false)
    const {locale} = useMultiSite()
    const localeId = locale.id.toLowerCase()
    const styles = useMultiStyleConfig('Subscriptions')
    const headingRef = useRef()
    const [refreshFlag, setRefreshFlag] = useState(0)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [isCancelled, setIsCancelled] = useState(false)
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const {formatMessage} = useIntl()
    // const location = useLocation()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Cancel Subscription',
    //     description: 'Cancel your subscription',
    //     url: currentPath,
    // }

    useEffect(() => {
        const fetchDetails = async () => {
            if (!subscriptionId) return
            try {
                setIsLoading(true)
                const token = await getTokenWhenReady()
                // Use the token from getTokenWhenReady for Commerce Cloud APIs
                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-subscription-details-oms?subscriptionId=${subscriptionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            customerId: customer?.customerId,
                            'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                        },
                    },
                )
                const result = await response.json()
                if (result.success) {
                    setSubscriptionDetails(result.subscription)
                    const cancelled = result.subscription.status?.toLowerCase() === 'cancelled'
                    setIsCancelled(cancelled)
                    if (cancelled) {
                        setSelectedReason('')
                    }
                }
            } catch (e) {
                // handle error
            } finally {
                setIsLoading(false)
            }
        }
        fetchDetails()
    }, [subscriptionId, refreshFlag])

    // Handle the cancel subscription action
    const handleCancelSubscription = async () => {
        try {
            setIsLoading(true)

            const cancelSubscriptionPayload = {
                allOrNone: true,
                compositeRequest: [
                    {
                        method: 'PATCH',
                        url: `/services/data/v59.0/sobjects/Subscription__c/${subscriptionId}`,
                        referenceId: 'refSubscription',
                        body: {
                            Status__c: 'Canceled', // Set the status to Canceled
                            CancelationReason__c: selectedReason,
                            CancelationDate__c: new Date(),
                        },
                    },
                ],
            }

            // Call the cancel-subscription-oms API
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/cancel-subscription-oms`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(cancelSubscriptionPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                setRefreshFlag((f) => f + 1)
                setShowCancelModal(true)
                setIsCancelled(true)
                setSelectedReason('')
                //setMessage('Cancel order created successfully')
            } else {
                //setMessage('Failed to create cancel order: ' + result.error)
            }
        } catch (error) {
            console.error('Error during cancel order creation:', error)
            //setMessage('Error during cancel order creation: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Stack gap="0" data-testid="test-cancel-subscription-page">
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Seo title="Cancel Subscription" description="Cancel your subscription" />
            <Text
                {...styles.subscriptionDetailsLabel}
                marginTop={remCalc(24)}
                as={Link}
                to={`/${localeId}/account/subscription-details/${subscriptionId}`}
            >
                <FormattedMessage
                    id="account_subscription_cancel.back_button"
                    defaultMessage="Back to subscription detail"
                />
            </Text>
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                subscriptionDetails && (
                    <>
                        <Text
                            as="h1"
                            {...styles.accountTitleDetailsSubscriptionCancel}
                            tabIndex="0"
                            ref={headingRef}
                        >
                            <FormattedMessage
                                defaultMessage="Confirm subscription cancellation"
                                id="account_subscription_cancel.title"
                            />
                        </Text>
                        <Box
                            {...styles.subscriptionCancelBoxContainer}
                            {...styles.subscriptionCancelGap}
                        >
                            <Text {...styles.assistanceTitle}>
                                <FormattedMessage
                                    id="account_subscription_cancel.date"
                                    defaultMessage="Date"
                                />
                                {': '}
                                {subscriptionDetails.subscriptionStartDate
                                    ? new Date(
                                          subscriptionDetails.subscriptionStartDate,
                                      ).toLocaleDateString(localeId)
                                    : ''}
                            </Text>
                            <Text {...styles.subscriptionNumberHeader}>
                                <FormattedMessage
                                    id="account_subscription_cancel.subscription_number"
                                    defaultMessage="Subscription number"
                                />
                                {': '}
                                {subscriptionDetails.name}
                            </Text>
                            <Text>
                                <FormattedMessage
                                    id="account_subscription_cancel.info"
                                    defaultMessage="You may cancel your order for a short time after creation. In order to do so, please state the reason for the cancellation."
                                />
                            </Text>
                            <Flex gap={4} align="center" wrap="wrap">
                                <Box
                                    {...styles.cancelSubscriptionSelectContainer}
                                    width={{base: '100%', md: remCalc(390)}}
                                >
                                    <Select
                                        placeholder={formatMessage({
                                            id: 'account_subscription_cancel.select_reason_placeholder',
                                            defaultMessage: 'Select reason',
                                        })}
                                        icon={<ChevronDown />}
                                        isRequired
                                        value={selectedReason}
                                        onChange={(e) => setSelectedReason(e.target.value)}
                                        {...styles.cancelSubscriptionSelect}
                                        width={{base: '100%', md: remCalc(390)}}
                                        isDisabled={isCancelled}
                                    >
                                        <option value="01">
                                            <FormattedMessage
                                                id="account_subscription_cancel.reason.changed_mind"
                                                defaultMessage="Change of mind"
                                            />
                                        </option>
                                        <option value="02">
                                            <FormattedMessage
                                                id="account_subscription_cancel.reason.dissatisfaction"
                                                defaultMessage="Dissatisfaction with the subscription service"
                                            />
                                        </option>
                                        <option value="03">
                                            <FormattedMessage
                                                id="account_subscription_cancel.reason.accidental_signup"
                                                defaultMessage="Accidental signup"
                                            />
                                        </option>
                                    </Select>
                                </Box>
                                <Button
                                    type="submit"
                                    isDisabled={!selectedReason || isCancelled}
                                    variant="primary"
                                    width={{base: '100%', lg: remCalc(332)}}
                                    right="0"
                                    alignSelf="flex-end"
                                    onClick={handleCancelSubscription}
                                >
                                    <FormattedMessage
                                        id="account_subscription_cancel.confirm_button"
                                        defaultMessage="Cancel subscription"
                                    />
                                </Button>
                            </Flex>
                        </Box>
                        <Box {...styles.subscriptionBoxContainer} {...styles.subscriptionCancelGap}>
                            {subscriptionDetails?.subscriptionItems?.map((item, idx) => (
                                <SubscriptionCancelItemCard
                                    key={item.id || idx}
                                    item={item}
                                    showDivider={
                                        idx < subscriptionDetails.subscriptionItems.length - 1
                                    }
                                />
                            ))}
                        </Box>
                    </>
                )
            )}
            <SubscriptionCancellationModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                subscriptionId={subscriptionId}
            />
        </Stack>
    )
}

export default SubscriptionCancel
